import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import {
  DSButton,
  DSInput,
  DSLinkButton,
} from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { useAccount } from "@hooks";
import { TextareaAutosize } from "@mui/base";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import TeamClinic from "../../../assets/imgs/team-clinic.png";
import TeamModal from "../TeamModal";

const REQUEST_TEAM_SETUP = gql`
  mutation requestTeamSetup(
    $email: String!
    $customMessage: String!
  ) {
    requestTeamSetup(email: $email, customMessage: $customMessage) {
      ok
    }
  }
`;

export function EmptyState({ orgUuid, refetch, currentUser }) {
  const [requestTeamSetup] = useMutation(REQUEST_TEAM_SETUP);
  const [searchParams, setSearchParams] = useSearchParams();
  const createTeam = searchParams.has("createTeam");
  const [shouldShowCreateTeamModal, setShouldShowCreateTeamModal] =
    useState(createTeam);
  const [
    shouldShowInviteManagerModal,
    setShouldShowInviteManagerModal,
  ] = useState(false);
  const { accountType } = useAccount();
  const { control, handleSubmit } = useForm();

  const onSubmit = async ({ email, customMessage }) => {
    try {
      await requestTeamSetup({
        variables: {
          email,
          customMessage,
        },
      });
      alert("success", `Invite sent to ${email}!`);
    } catch (error) {
      alert("error", `Failed to send invite: ${error.message}`);
    }
    setShouldShowInviteManagerModal(false);
  };

  const hideCreateTeamModal = () => {
    const currentParams = searchParams;
    currentParams.delete("createTeam");
    setSearchParams(currentParams);
    setShouldShowCreateTeamModal(false);
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <TeamModal
        isOpen={shouldShowCreateTeamModal}
        onClose={hideCreateTeamModal}
        orgIsManualBilling={false}
        hasBillingSetup={accountType === "unlimited"}
        orgUuid={orgUuid}
        isCreatingTeam={true}
        refetch={refetch}
      />
      <DSModal
        isOpen={shouldShowInviteManagerModal}
        onClose={() => setShouldShowInviteManagerModal(false)}
        title="Invite Your Practice Manager"
        subtitle="Send a message to your practice manager so they can create a team for you."
        dataTestId="invite-manager-modal"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => setShouldShowInviteManagerModal(false),
          },
          {
            label: "Send Invite",
            variant: "primary",
            onClick: handleSubmit(onSubmit),
          },
        ]}
      >
        <div>
          <form className="space-y-md">
            <div>
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <DSInput
                    label="Practice Manager's Email"
                    placeholder="Enter your practice manager's email"
                    dataTestId="practice-manager-email-input"
                    {...field}
                  />
                )}
              />
            </div>
            <div>
              <label className="my-sm font-semibold text-primary">
                Custom Message
              </label>
              <Controller
                control={control}
                name="customMessage"
                defaultValue={
                  "Hi! I'm inviting you to create a team for our clinic on Scribenote. This is so we can share notes and templates among our care team and have centralized account management."
                }
                render={({ field }) => (
                  <TextareaAutosize
                    minRows={3}
                    className="text-sm w-full appearance-none resize-none bg-input rounded-standard p-3"
                    {...field}
                  />
                )}
              />
            </div>
          </form>
        </div>
      </DSModal>
      <div className="flex flex-col items-center space-y-10 p-10 rounded-large">
        <div className="flex flex-col text-center items-center space-y-sm">
          <h2>Welcome to Teams Mode!</h2>
          <DSLinkButton
            icon={faQuestionCircle}
            onClick={() =>
              window.open(
                "https://docs.scribenote.com/en/articles/8618740-teams-mode",
                "_blank",
              )
            }
            dataTestId="whats-this-link"
          >
            What's This?
          </DSLinkButton>
        </div>

        <img
          src={TeamClinic}
          alt="Teams Mode Empty State"
          className="w-1/2"
        />
        <h5 className="max-w-3xl text-center">
          Teams Mode allows you to share notes and templates with
          fellow clinic staff. Let's get started!
        </h5>
        <div className="flex flex-row space-x-sm">
          {currentUser?.user?.role?.id !== "2" && (
            <DSButton
              variant="primary"
              size="lg"
              onClick={() => setShouldShowInviteManagerModal(true)}
              dataTestId="invite-manager-button"
            >
              I want my practice manager to set up a team
            </DSButton>
          )}
          <DSButton
            variant={
              currentUser?.user?.role?.id === "2"
                ? "primary"
                : "secondary"
            }
            size="lg"
            onClick={() => setShouldShowCreateTeamModal(true)}
            dataTestId={
              currentUser?.user?.role?.id === "2"
                ? "create-team-button"
                : "setup-team-button"
            }
          >
            {currentUser?.user?.role?.id === "2"
              ? "+ Create a Team"
              : "I'll set one up"}
          </DSButton>
        </div>
      </div>
    </div>
  );
}
